import { datadogRum } from '@datadog/browser-rum'
import { Box } from 'core/components/Box'
import WithEncodingReadingBase, { WithEncodingReadingState } from 'core/pages/Shipping/WithEncoding/WithEncodingReading'
import {
  ConfigurationDetail,
  EncodingValidationRequest,
  IdentifierType,
  ProductInfoField
} from 'stylewhere/api'
import { Text } from 'stylewhere/components'
import { AppStore, RfidReader } from 'stylewhere/shared'
import { T, __ } from 'stylewhere/shared/i18n'

export interface LVCustomShippingWithEncodingReadingState extends WithEncodingReadingState {
  lastConfirmedParcel: string
}

export default class LVCustomShippingWithEncodingReading extends WithEncodingReadingBase {
  declare state: LVCustomShippingWithEncodingReadingState

  async componentDidMount() {
    await super.componentDidMount()
    // Initialize lastConfirmedParcel if not already set
    if (!this.state.lastConfirmedParcel) {
      this.setState({ lastConfirmedParcel: '' } as any)
    }
    await RfidReader.initialize()
    await RfidReader.fillRfidAntennas(AppStore.defaultWorkstation!)
    const lastConfirmedParcel = localStorage.getItem(
      `rfid-station::op_${this.operation.id}_custom_lvShippingWithEncodingLastConfirmedParcel`
    )
    if (lastConfirmedParcel) {
      this.setState({ lastConfirmedParcel: lastConfirmedParcel } as any)
    }
  }

  onStopCallbackIdentifierCheck(tmpEncoding: EncodingValidationRequest) {
    if (
      tmpEncoding &&
      tmpEncoding.identifiers &&
      // nfc collision
      (tmpEncoding.identifiers.some((idt) => idt._status === 'NFC_COLLISION') ||
        // nfc auth timeout
        tmpEncoding.identifiers.some(
          (idt) => idt._status === 'ERROR' && idt._error && [__(T.error.authentication_timeout)].includes(idt._error)
        ) ||
        // uhf errors
        tmpEncoding.identifiers.some((idt) => idt.identifierType === 'UHF_TAG' && idt._status === 'ERROR'))
    ) {
      tmpEncoding.identifiers.forEach((element) => {
        if (!element._status || element._status === 'PROCESSING') element._status = 'SKIPPED'
      })
      return false
    }

    // default
    return (
      tmpEncoding && tmpEncoding.identifiers && !tmpEncoding.identifiers.every((idt) => idt._status === 'CONFIRMED')
    )
  }

  checkTooManyUhfTagsError = async (encodingValidation: EncodingValidationRequest): Promise<boolean> => {
    if (encodingValidation.identifiers.filter((id) => id.identifierType === 'UHF_TAG').length <= 1) {
      return false
    }
    const newEncodingValidation = {
      ...encodingValidation,
      identifiers: encodingValidation.identifiers.filter((id) => id.identifierType !== 'UHF_TAG')
    }
    const uhfIdentifier = encodingValidation.identifiers.find((id) => id.identifierType === 'UHF_TAG')!
    uhfIdentifier._error = __(T.custom.tooManyTags)
    uhfIdentifier._status = 'ERROR'
    newEncodingValidation.identifiers.push(uhfIdentifier)
    this.setState({ encodingValidation: newEncodingValidation, associationStatus: 'ERROR' })
    if (this.datadogSession) {
      datadogRum.addAction('debug_stopAntennaTooManyUhfTags', { ...this.datadogSession, encodingValidation })
    }
    await this.stopAntenna()
    this.clearTimer()
    this.clearTimerReader()
    this.isEncoding = false
    return true
  }

  getErrorCodesToWhichStopAntenna = () => {
    return ['EXTRA_TAG', 'WRONG_PRODUCT', 'WRONG_MADEIN', 'UNKNOWN_TAG']
  }

  confirmButtonEnabled = () => {
    if (this.state.autoConfirmParcel) {
      return this.state.initReaderTags.length > 0
    }
    return this.parcelIsSuitableForConfirmation()
  }

  getFilteredIdentifiersToRender = (identifiers: ConfigurationDetail[]) => {
    if (AppStore.getEmulation()) return identifiers
    return identifiers.filter((idt) => idt.role !== 'SERIAL')
  }

  getHideIdentifierByType = (): IdentifierType[] | undefined => {
    return ['NFC_TAG', 'UHF_TAG']
  }

  getHideIdentifierCodeByType = (): IdentifierType[] | undefined => {
    return ['NFC_TAG', 'UHF_TAG']
  }

  getIdentifiersLabelsByType: () => Partial<Record<IdentifierType, string>> | undefined = () => {
    return { ['UHF_TAG']: __(T.misc.external_tag), ['NFC_TAG']: __(T.misc.internal_tag) }
  }

  getDisableRetry() {
    return true
  }

  getProductInfoFields: () => ProductInfoField[] | undefined = () => {
    return []
  }

  getHideIdentifierZones: () => boolean = () => {
    return true
  }

  onConfirmSuccess = async (result?) => {
    if (result?.code) {
      localStorage.setItem(
        `rfid-station::op_${this.operation.id}_custom_lvShippingWithEncodingLastConfirmedParcel`,
        result.code
      )
      this.setState({ lastConfirmedParcel: result.code } as any)
    }
  }

  getCustomSidebarBottomComponent = () => {
    const { lastConfirmedParcel, formData } = this.state
    if (!lastConfirmedParcel || formData?.product?.code) return null
    return (
      <Box mb={10} mt={16} mh={2}>
        <Text bold data-testid="lv-shipping-with-encoding-last-confirmed-parcel">
          {__(T.misc.last_confirmed_ucs)}: {lastConfirmedParcel}
        </Text>
      </Box>
    )
  }

  enabledResetOnlyProduct = () => {
    return false
  }

  render() {
    return super.render()
  }
}
